import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { OEP_ENTITY } from 'agu_oep_core';
import { OEPViewBase } from '../state/oep-view-base';
import { OEPCaptureOpener, OEPRestoreFocus, OEPTrapTab } from './dialog-focus';
import { BuildEmailEntries, OEP_TEMPLATE_FILTER, type OEPEmailEntry, type OEPTemplateContentRow } from './oep-emails.vm';

/**
 * Staff "Notification emails" (markup.html:970-993) in mj-slide-panel Mode="dialog". One entry per
 * OEP_EMAILS key: trigger, subject as written, and the template body loaded through RunView on
 * 'MJ: Template Contents' once per session (a failed load retries on the next open) (OEP Staff reads it — tools/metadata/oep-permissions.js). The body is
 * rendered as text (HtmlToText), never as HTML. Opened from the notifications panel and from
 * Program status; focus returns to whichever control opened it.
 */
@Component({
    standalone: false,
    selector: 'oep-emails',
    templateUrl: './oep-emails.component.html',
    styleUrl: './oep-emails.component.scss',
})
export class OEPEmailsComponent extends OEPViewBase implements OnChanges {
    @Input() Open = false;
    @Output() Close = new EventEmitter<void>();
    /** A stable connected element (the header bell) for focus to return to when the opener has been removed. */
    @Input() FocusFallback: (() => HTMLElement | null) | null = null;
    Entries: OEPEmailEntry[] = [];
    Loaded = false;
    LoadFailure: string | null = null;
    private opener: HTMLElement | null = null;

    /** Moves focus onto the dialog container when it renders (the panel mounts with @if Open). */
    @ViewChild('Dialog') set Dialog(el: ElementRef<HTMLElement> | undefined) {
        el?.nativeElement.focus();
    }

    /** Nothing to load at init; the panel loads when it opens. */
    protected async Load(): Promise<void> { /* on open, see LoadTemplates */ }

    ngOnChanges(changes: SimpleChanges): void {
        const open = changes['Open'];
        if (!open) return;
        if (this.Open) {
            this.opener = OEPCaptureOpener(document);
            if (!this.Cached) void this.LoadTemplates();
        } else if (!open.firstChange) {
            OEPRestoreFocus(this.opener, this.FocusFallback?.() ?? null);
            this.opener = null;
        }
    }

    /** Templates are loaded once per session; a failed load leaves this false so the next open retries. */
    private Cached = false;

    private async LoadTemplates(): Promise<void> {
        this.Loaded = false;
        this.LoadFailure = null;
        this.Changed();
        try {
            const rows = await this.Data.Rows<OEPTemplateContentRow>(OEP_ENTITY.TemplateContents, OEP_TEMPLATE_FILTER, ['TemplateID', 'TemplateText', 'Template']);
            this.Entries = BuildEmailEntries(rows);
            this.Cached = true;
        } catch (err) {
            this.Entries = [];
            this.LoadFailure = err instanceof Error ? err.message : String(err);
        } finally {
            this.Loaded = true;
            this.Changed();
        }
    }

    TrapTab(event: KeyboardEvent): void {
        OEPTrapTab(event, event.currentTarget as HTMLElement);
    }
}
